import React from 'react';
import classNames from 'classnames';

import useStore from 'Hooks/useStore';
import LegOptionWaitlist from 'Modules/MultiStopWizard/Components/Leg/LegOptionWaitlist';
import { LegOptionContext } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import { Display } from 'Views/Components/Button/Button';
import { deselectProps, hasDeparted, isSoldOut, tripOptionStateClasses } from './tripOptionState';
import TripOptionChangeButton from './TripOptionChangeButton';
import {
	TripOptionChosenLabel,
	TripOptionPrice,
	TripOptionAside,
	TripOptionSoldOutNote,
	TripOptionSpace,
	TripOptionTimes,
	TripOptionVoyage,
	TripOptionWhen,
} from './TripOptionParts';


/**
 * Chips shown on a phone before the rest fold behind `+N more`. The chips get the left column only,
 * beside the price, so two named capacities and the `+N` chip is about as much as fits before the
 * card is mostly capacity.
 */
const MOBILE_VISIBLE_CHIPS = 2;

/**
 * A sailing at phone width — `20a` in the list, `9b` once chosen.
 *
 * <p>The row stops being a row. Four bands across 390px are 97px each, which fits neither a pair of
 * times, a set of capacity chips, nor a price, so each sailing becomes a small card: the times as
 * its header, everything else stacked under them.</p>
 *
 * <p>The chosen card puts its way out underneath rather than inside. A full-width button is the
 * phone's own idiom and the thumb reaches the bottom of the card more easily than a link inside
 * it — which is the opposite of the desktop, where the footer link keeps the frame whole.</p>
 */
export default function TripOptionMobile() {
	const context = React.useContext(LegOptionContext);
	const { isStaff } = useStore();

	if (!context) {
		console.error('LegOptionContext is missing in TripOptionMobile');
		return null;
	}

	const { isSelected, isSelectable, onClick } = context;

	/*
		What sits where the fare goes, on a sailing a customer cannot take.

		A sailing that has GONE quotes nothing: the figure is not an offer, and on a phone — where
		the row is three lines rather than four columns — `From $139.00` beside `DEPARTED` is the
		largest thing on a row the booker is meant to skip.

		A sailing that is SOLD OUT gets its waitlist button there instead. Same reasoning, one step
		on: the fare is not an offer either, and the button is the only thing on that row a customer
		can act on, so it takes the place the eye already goes to. Note a site with the waitlist
		switched off then shows the status alone — which is the honest answer, since there is
		nothing to sell and nothing to join.

		Staff keep the fare in both cases: they read these rows to answer what a sailing went out at.
	*/
	const waitlistInsteadOfPrice = isSoldOut(context) && !isStaff;
	const hidePrice = waitlistInsteadOfPrice || (hasDeparted(context) && !isStaff);

	if (isSelected) {
		return (
			<div
				className="trip-option-shell trip-option-shell--selected"
				data-test-id="trip-option-selected"
				data-trip-id={context.trip.id}
			>
				<div
					className="trip-option trip-option--mobile trip-option--selected"
					{...deselectProps(onClick)}
				>
					<div className="trip-option__head">
						<TripOptionChosenLabel />
						<TripOptionTimes size="md" />
						<span className="trip-option__when-row">
							<TripOptionWhen />
							<TripOptionSoldOutNote inline />
						</span>
					</div>

					<div className="trip-option__body">
						<TripOptionSpace
							collapseChipsAfter={MOBILE_VISIBLE_CHIPS}
							aside={
								<TripOptionAside>
									<TripOptionPrice size="md" />
								</TripOptionAside>
							}
						/>
					</div>
				</div>

				{/*
					Inside the frame, as a link — the same control the wide card carries.

					It began as a full-width outlined button under the card, on the grounds that a
					thumb reaches the bottom edge more easily. That is true and it still read as a
					second object: a bordered card, a gap, another bordered box. One frame with a
					footer is the same thing the desktop shows, and the same thing a booker has just
					seen in the list.
				*/}
				<div className="trip-option__footer">
					<TripOptionChangeButton display={Display.Text} />
				</div>
			</div>
		);
	}

	return (
		<div
			className={classNames('trip-option', 'trip-option--mobile', tripOptionStateClasses(context))}
			data-test-id={`trip-option-${context.trip.id}`}
			data-trip-selectable={isSelectable ? 'true' : 'false'}
			data-trip-availability={context.availabilityStatus ?? 'unknown'}
			onClick={isSelectable ? onClick : undefined}
			onKeyDown={event => {
				if (isSelectable && (event.key === 'Enter' || event.key === ' ')) {
					event.preventDefault();
					onClick?.();
				}
			}}
			role="button"
			tabIndex={isSelectable ? 0 : -1}
			aria-disabled={!isSelectable}
		>
			<div className="trip-option__head">
				<TripOptionVoyage />
				<TripOptionTimes size="sm" />
				<TripOptionSoldOutNote />
			</div>

			<div className="trip-option__body">
				<TripOptionSpace
					/*
						The dot the design puts at the head of the body row. It stands in for the
						rail, which a 390px card has no room for — a 4px stripe down the side of a
						phone card is a stripe nobody reads.
					*/
					lead={<span className="trip-option__dot" aria-hidden="true" />}
					showWaitlist={!waitlistInsteadOfPrice}
					collapseChipsAfter={MOBILE_VISIBLE_CHIPS}
					aside={
						<TripOptionAside>
							{(() => {
								if (waitlistInsteadOfPrice) {
									return <LegOptionWaitlist />;
								}
								return hidePrice ? null : <TripOptionPrice size="sm" />;
							})()}
						</TripOptionAside>
					}
				/>
			</div>
		</div>
	);
}
